import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import type { DeskTemplateRef, DeskFinding, StudioDesk } from 'types/studio';
import StudioDeskOrganism from './StudioDeskOrganism';
import { StudioBuilder } from './builders';
import './StudioDeskPage.scss';

/**
 * Platform-mode home (spec SC1, UI Components table) — replaces `StudioHomePage` in
 * `StudioModeHome` for `mode === 'platform'`. Owns navigation/analytics for the desk's three
 * regions; `StudioDeskOrganism` owns the data fetch and per-region failure states.
 *
 * Resume and Registry-strip clicks are plain in-desk navigation — the desk IS the home, so
 * there is no `from` to record. A finding click carries `state.from = { kind: 'finding' }` (SC9)
 * so the target builder's Back affordance returns here.
 */
const StudioDeskPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logAnalyticsEvent = useAnalytics();

  const handleDeskLoaded = useCallback(
    (desk: StudioDesk) => {
      const findingsByKind = desk.findings.reduce<Record<string, number>>((acc, finding) => {
        acc[finding.kind] = (acc[finding.kind] ?? 0) + 1;
        return acc;
      }, {});
      logAnalyticsEvent('studio_desk_viewed', undefined, {
        recents: desk.recents.length,
        findingsByKind,
        degraded: desk.degraded,
      });
    },
    [logAnalyticsEvent]
  );

  const handleOpenResume = useCallback(
    (item: DeskTemplateRef, route: string) => {
      logAnalyticsEvent('studio_desk_resume_clicked', undefined, { type: item.type, status: item.status });
      navigate(route);
    },
    [navigate, logAnalyticsEvent]
  );

  const handleOpenFinding = useCallback(
    (finding: DeskFinding, route: string) => {
      logAnalyticsEvent('studio_finding_opened', undefined, {
        kind: finding.kind,
        code: finding.detail?.[0]?.code,
        type: finding.template.type,
      });
      navigate(route, { state: { entry: 'nav', from: { kind: 'finding', id: finding.template.templateId } } });
    },
    [navigate, logAnalyticsEvent]
  );

  const handleOpenBuilder = useCallback(
    (builder: StudioBuilder) => {
      navigate(`/studio/${builder.segment}`);
    },
    [navigate]
  );

  return (
    <div className="studio-desk-page" data-testid="studio-desk-page">
      <header className="studio-desk-page__header">
        <h1 className="studio-desk-page__title">{t('studio.desk.title')}</h1>
      </header>
      <StudioDeskOrganism
        onOpenResume={handleOpenResume}
        onOpenFinding={handleOpenFinding}
        onOpenBuilder={handleOpenBuilder}
        onDeskLoaded={handleDeskLoaded}
      />
    </div>
  );
};

export default StudioDeskPage;
